
import React from 'react';

const InfluenzaPreventionPharmacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              インフルエンザ予防に薬局でできること｜薬剤師が教える最新対策
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              2025年最新のインフルエンザ対策を薬剤師が詳しく解説
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="bg-blue-500 px-3 py-1 rounded-full">インフルエンザ 予防 薬局</span>
              <span className="bg-blue-500 px-3 py-1 rounded-full">インフルエンザ 対策 2025</span>
              <span className="bg-blue-500 px-3 py-1 rounded-full">薬剤師監修</span>
            </div>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* 記事情報 */}
          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                2025年10月30日
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                ビースト薬剤師
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                読了時間：約8分
              </span>
            </div>
          </div>

          {/* 目次 */}
          <div className="bg-blue-50 p-6 rounded-lg mb-8">
            <h2 className="text-xl font-bold mb-4 text-blue-800">目次</h2>
            <ul className="space-y-2">
              <li><a href="#overview" className="text-blue-600 hover:underline">1. インフルエンザ予防の基本知識</a></li>
              <li><a href="#pharmacy-role" className="text-blue-600 hover:underline">2. 薬局でできるインフルエンザ対策</a></li>
              <li><a href="#vaccination" className="text-blue-600 hover:underline">3. インフルエンザワクチンについて</a></li>
              <li><a href="#otc-medicines" className="text-blue-600 hover:underline">4. 市販薬による予防・対策</a></li>
              <li><a href="#lifestyle" className="text-blue-600 hover:underline">5. 日常生活での予防法</a></li>
              <li><a href="#consultation" className="text-blue-600 hover:underline">6. 薬剤師への相談のメリット</a></li>
            </ul>
          </div>

          {/* セクション1: インフルエンザ予防の基本知識 */}
          <section id="overview" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">インフルエンザ予防の基本知識</h2>
            
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
              <h3 className="text-lg font-bold text-red-800 mb-2">2025年のインフルエンザ流行予測</h3>
              <p className="text-red-700">
                2025年は新型コロナウイルスとの同時流行が予想されており、早期の予防対策が重要です。
                薬局での適切な相談と対策で、感染リスクを大幅に減らすことができます。
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-600">インフルエンザの特徴</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-500 mt-1"></i>
                    <span>突然の高熱（38℃以上）</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-500 mt-1"></i>
                    <span>全身の倦怠感・筋肉痛</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-500 mt-1"></i>
                    <span>咳・のどの痛み</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-500 mt-1"></i>
                    <span>頭痛・関節痛</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-orange-600">感染経路</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-line text-orange-500 mt-1"></i>
                    <span>飛沫感染（咳・くしゃみ）</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-line text-orange-500 mt-1"></i>
                    <span>接触感染（手すり・ドアノブ）</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-line text-orange-500 mt-1"></i>
                    <span>空気感染（密閉空間）</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* セクション2: 薬局でできるインフルエンザ対策 */}
          <section id="pharmacy-role" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">薬局でできるインフルエンザ対策</h2>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold mb-3 text-blue-800">予防相談</h3>
                <p className="text-blue-700 text-sm">
                  個人の健康状態に合わせた予防法をアドバイス。持病や服用中の薬との相互作用も考慮します。
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-medicine-bottle-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold mb-3 text-green-800">市販薬選択</h3>
                <p className="text-green-700 text-sm">
                  症状や体質に適した市販薬の選択をサポート。副作用や注意点も詳しく説明します。
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-heart-pulse-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold mb-3 text-purple-800">健康管理</h3>
                <p className="text-purple-700 text-sm">
                  免疫力向上のためのサプリメントや生活習慣のアドバイスを提供します。
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-yellow-800">薬局での感染対策サービス</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-double-line text-green-500"></i>
                    <span>手指消毒剤の設置</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-double-line text-green-500"></i>
                    <span>マスクの適切な選び方指導</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-double-line text-green-500"></i>
                    <span>体温測定サービス</span>
                  </li>
                </ul>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-double-line text-green-500"></i>
                    <span>健康相談窓口</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-double-line text-green-500"></i>
                    <span>予防接種の情報提供</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-double-line text-green-500"></i>
                    <span>医療機関への紹介</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* セクション3: インフルエンザワクチンについて */}
          <section id="vaccination" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">インフルエンザワクチンについて</h2>

            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-bold mb-4 text-blue-800">2025年のワクチン情報</h3>
              <p className="text-blue-700 mb-4">
                2025年のインフルエンザワクチンは、A型2株・B型2株の4価ワクチンです。
                WHO推奨株に基づいて製造されており、流行予測株に対応しています。
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded">
                  <h4 className="font-bold text-blue-700 mb-2">接種時期</h4>
                  <p className="text-sm text-gray-700">10月〜12月（流行前）</p>
                </div>
                <div className="bg-white p-4 rounded">
                  <h4 className="font-bold text-blue-700 mb-2">効果持続期間</h4>
                  <p className="text-sm text-gray-700">約5ヶ月間</p>
                </div>
                <div className="bg-white p-4 rounded">
                  <h4 className="font-bold text-blue-700 mb-2">予防効果</h4>
                  <p className="text-sm text-gray-700">60〜90%（年齢・健康状態による）</p>
                </div>
                <div className="bg-white p-4 rounded">
                  <h4 className="font-bold text-blue-700 mb-2">接種回数</h4>
                  <p className="text-sm text-gray-700">13歳以上：1回、13歳未満：2回</p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-6">
              <h3 className="text-lg font-bold text-orange-800 mb-2">薬剤師からのアドバイス</h3>
              <p className="text-orange-700">
                ワクチン接種後も手洗い・うがい・マスク着用などの基本的な予防対策は継続してください。
                ワクチンは重症化予防に特に効果的です。
              </p>
            </div>
          </section>

          {/* セクション4: 市販薬による予防・対策 */}
          <section id="otc-medicines" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">市販薬による予防・対策</h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-green-600">予防に役立つ市販薬</h3>
                <div className="space-y-4">
                  <div className="border-b pb-3">
                    <h4 className="font-bold text-gray-800">うがい薬</h4>
                    <p className="text-sm text-gray-600">ポビドンヨード系、アズレン系</p>
                    <p className="text-xs text-green-600">のどの殺菌・消毒効果</p>
                  </div>
                  <div className="border-b pb-3">
                    <h4 className="font-bold text-gray-800">ビタミンC・亜鉛</h4>
                    <p className="text-sm text-gray-600">免疫力向上サプリメント</p>
                    <p className="text-xs text-green-600">免疫機能のサポート</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">乳酸菌製剤</h4>
                    <p className="text-sm text-gray-600">腸内環境改善</p>
                    <p className="text-xs text-green-600">免疫力の基盤作り</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-red-600">初期症状の対処薬</h3>
                <div className="space-y-4">
                  <div className="border-b pb-3">
                    <h4 className="font-bold text-gray-800">解熱鎮痛薬</h4>
                    <p className="text-sm text-gray-600">アセトアミノフェン、イブプロフェン</p>
                    <p className="text-xs text-red-600">発熱・頭痛・筋肉痛に</p>
                  </div>
                  <div className="border-b pb-3">
                    <h4 className="font-bold text-gray-800">咳止め薬</h4>
                    <p className="text-sm text-gray-600">デキストロメトルファン系</p>
                    <p className="text-xs text-red-600">乾いた咳の緩和</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">のど飴・トローチ</h4>
                    <p className="text-sm text-gray-600">殺菌成分配合</p>
                    <p className="text-xs text-red-600">のどの痛み・乾燥対策</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-red-800 mb-3">
                <i className="ri-alert-line mr-2"></i>
                重要な注意点
              </h3>
              <ul className="space-y-2 text-red-700">
                <li>• 高熱が続く場合は早めに医療機関を受診してください</li>
                <li>• 市販薬は症状緩和が目的で、根本的な治療ではありません</li>
                <li>• 他の薬との飲み合わせに注意が必要です</li>
                <li>• 妊娠中・授乳中の方は薬剤師にご相談ください</li>
              </ul>
            </div>
          </section>

          {/* セクション5: 日常生活での予防法 */}
          <section id="lifestyle" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">日常生活での予防法</h2>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold mb-4 text-blue-800">基本的な感染対策</h3>
                <ul className="space-y-2 text-blue-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-hand-sanitizer-line text-blue-500"></i>
                    <span>こまめな手洗い・消毒</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-mask-line text-blue-500"></i>
                    <span>適切なマスク着用</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-home-line text-blue-500"></i>
                    <span>室内の換気</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-group-line text-blue-500"></i>
                    <span>人混みを避ける</span>
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold mb-4 text-green-800">免疫力向上</h3>
                <ul className="space-y-2 text-green-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-restaurant-line text-green-500"></i>
                    <span>バランスの良い食事</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-zzz-line text-green-500"></i>
                    <span>十分な睡眠</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-run-line text-green-500"></i>
                    <span>適度な運動</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-emotion-line text-green-500"></i>
                    <span>ストレス管理</span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold mb-4 text-orange-800">環境対策</h3>
                <ul className="space-y-2 text-orange-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-temp-hot-line text-orange-500"></i>
                    <span>適切な室温（20-25℃）</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-drop-line text-orange-500"></i>
                    <span>湿度管理（50-60%）</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-plant-line text-orange-500"></i>
                    <span>空気清浄機の活用</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-sun-line text-orange-500"></i>
                    <span>日光浴でビタミンD生成</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-4 text-purple-800">薬剤師おすすめの予防習慣</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-bold text-purple-700 mb-2">朝の習慣</h4>
                  <ul className="text-sm space-y-1 text-purple-600">
                    <li>• 起床後のうがい・手洗い</li>
                    <li>• ビタミンCサプリメントの摂取</li>
                    <li>• 体温測定</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-purple-700 mb-2">夜の習慣</h4>
                  <ul className="text-sm space-y-1 text-purple-600">
                    <li>• 帰宅後の手洗い・うがい</li>
                    <li>• 十分な水分補給</li>
                    <li>• 早めの就寝</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* セクション6: 薬剤師への相談のメリット */}
          <section id="consultation" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">薬剤師への相談のメリット</h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4 text-indigo-800">個別対応の専門アドバイス</h3>
                <ul className="space-y-3 text-indigo-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-user-heart-line text-indigo-500 mt-1"></i>
                    <span>年齢・体質に合わせた予防法の提案</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-medicine-bottle-line text-indigo-500 mt-1"></i>
                    <span>服用中の薬との相互作用チェック</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-heart-pulse-line text-indigo-500 mt-1"></i>
                    <span>持病を考慮した安全な対策</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4 text-emerald-800">継続的なサポート</h3>
                <ul className="space-y-3 text-emerald-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-calendar-check-line text-emerald-500 mt-1"></i>
                    <span>定期的な健康状態の確認</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-phone-line text-emerald-500 mt-1"></i>
                    <span>症状悪化時の迅速な対応</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-hospital-line text-emerald-500 mt-1"></i>
                    <span>必要時の医療機関紹介</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-yellow-800 mb-3">
                <i className="ri-lightbulb-line mr-2"></i>
                薬剤師に相談すべきタイミング
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded">
                  <h4 className="font-bold text-yellow-700 mb-2">予防段階</h4>
                  <p className="text-sm text-gray-700">流行前の対策相談</p>
                </div>
                <div className="bg-white p-4 rounded">
                  <h4 className="font-bold text-yellow-700 mb-2">初期症状</h4>
                  <p className="text-sm text-gray-700">軽い症状の対処法</p>
                </div>
                <div className="bg-white p-4 rounded">
                  <h4 className="font-bold text-yellow-700 mb-2">回復期</h4>
                  <p className="text-sm text-gray-700">再発防止の相談</p>
                </div>
              </div>
            </div>
          </section>

          {/* まとめ */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">まとめ</h2>
              <p className="text-blue-100 mb-4">
                2025年のインフルエンザ対策は、ワクチン接種と日常的な予防対策の組み合わせが重要です。
                薬局では専門的な知識を持つ薬剤師が、個人の状況に合わせた最適な予防法をアドバイスします。
              </p>
              <div className="bg-blue-500 p-4 rounded">
                <h3 className="font-bold mb-2">薬剤師からの最終アドバイス</h3>
                <p className="text-sm text-blue-100">
                  インフルエンザ予防は継続が鍵です。不安なことがあれば、いつでもお近くの薬局にご相談ください。
                  早期の対策で、健康な冬を過ごしましょう。
                </p>
              </div>
            </div>
          </section>

          {/* 関連記事 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">関連記事</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <a href="/beast-blog/winter-mental-health" className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <h3 className="font-bold text-blue-600 mb-2">冬季うつ病対策</h3>
                <p className="text-sm text-gray-600">冬の心の健康管理について</p>
              </a>
              <a href="/beast-blog/otc-selection" className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <h3 className="font-bold text-blue-600 mb-2">市販薬の選び方</h3>
                <p className="text-sm text-gray-600">症状に合った薬の選択法</p>
              </a>
              <a href="/beast-blog/health-management" className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <h3 className="font-bold text-blue-600 mb-2">健康管理のコツ</h3>
                <p className="text-sm text-gray-600">日常的な健康維持方法</p>
              </a>
            </div>
          </section>
        </div>
      </main>

      {/* フッター */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">
            この記事は薬剤師が監修しています。個別の症状については、必ず医療機関にご相談ください。
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InfluenzaPreventionPharmacyPage;
